import { Check } from "lucide-react"

import { cn } from "@/lib/utils"

// Horizontal numbered progress rail for the Post-a-Project wizard.
//
// Props:
//   steps           — [{ id, title | label }]  (render order)
//   activeIndex     — index of the step currently shown
//   completedThrough — index of the last *completed* step (-1 = none)
//   onJump(index)   — optional; called when a completed step is clicked.
//                     Only completed steps (i <= completedThrough, not the
//                     active one) are made clickable.
//   collapseAt      — 1199 | 1080 | 700 | null (default). Below this width the
//                     node rail hides and the compact "Step n of N · label"
//                     progress strip renders instead (P8-H5 "Footlights",
//                     design prototype `.jf-stepmini` / `.sf-stepmini`).
//                     null = the rail never collapses (the add-on stage's
//                     single node cannot clip).
//
// Visual: completed = filled primary + check, active = primary ring,
// future = muted. Labels collapse on narrow screens; numbers always show.

// Per-instance collapse boundary → the non-overlapping Tailwind band pair that
// hides the node rail below it and the compact strip at/above it. The class
// strings are LITERAL on purpose: Tailwind v4 scans source text, so a computed
// `max-[${n}px]:hidden` would never be emitted. Each boundary owns exactly one
// property on each element (display), so the v4 arbitrary-variant emission
// order can never make two rules contest one another.
//
// The literal in the class is the EXCLUSIVE edge, not the boundary: Tailwind v4
// emits `max-[Npx]` as `@media (width < N)`, so a rail that must be gone AT 700
// is written `max-[701px]`. Each pair below is the exact complement of the
// other (`width < N+1` and `width >= N+1`), so no width falls in both or
// neither band.
const COLLAPSE_BANDS = {
  1199: { rail: "max-[1200px]:hidden", mini: "min-[1200px]:hidden" },
  1080: { rail: "max-[1081px]:hidden", mini: "min-[1081px]:hidden" },
  700: { rail: "max-[701px]:hidden", mini: "min-[701px]:hidden" },
}

// The frozen per-live-instance mapping (P8-H5 contract + the POST-SCREENS
// registry). One live stepper instance at a time, so each caller passes exactly
// one of these:
//
//   picker      S-POST-PICKER P1–P3 and S-POST-HUB — the 3-node
//               "Path · Select · Set up" rail, collapses at 700.
//   bookWriting S-POST-BOOKWRITING — the 7-step manuscript rail clips inside
//               its own scroller at tablet-landscape, so it collapses at 1199.
//               Do NOT lower this to 1080.
//   service     S-POST-SERVICE — every other service config (incl. the
//               bundle-queued ones), collapses at 1080 with the layout.
//   addon       S-POST-ADDON — a single node cannot clip, so it never collapses.
//
// S-POST-PICKER P4 (focused naming), S-POST-REVIEW and S-TOOLS-PRICING-CALC
// mount no stepper at all, at any width.
export const STEPPER_COLLAPSE = {
  picker: 700,
  bookWriting: 1199,
  service: 1080,
  addon: null,
}

export function Stepper({ steps = [], activeIndex = 0, completedThrough = -1, onJump, collapseAt = null }) {
  const labelOf = (step, i) => step.label ?? step.title ?? `Step ${i + 1}`
  const band = COLLAPSE_BANDS[collapseAt] ?? null

  const total = steps.length
  const position = Math.min(Math.max(activeIndex, 0), Math.max(total - 1, 0))
  const pct = total > 0 ? Math.round(((position + 1) / total) * 100) : 0

  return (
    <>
      {band && (
        <div
          data-testid="stepper-mini"
          className={cn("rounded-[14px] border border-border bg-card px-4 py-[13px]", band.mini)}
        >
          <div className="flex min-w-0 items-baseline gap-2 text-[12.5px]">
            <b className="whitespace-nowrap font-display font-bold text-foreground">
              Step {position + 1} of {total}
            </b>
            <span className="truncate font-semibold text-muted-foreground">
              · {labelOf(steps[position] ?? {}, position)}
            </span>
          </div>
          <div className="mt-2.5 h-[5px] overflow-hidden rounded-[5px] bg-secondary">
            <i className="block h-full rounded-[5px] bg-foreground transition-[width] duration-200" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      <div
        data-testid="stepper-rail"
        className={cn(
          "flex items-center overflow-x-auto rounded-2xl border border-border bg-card px-5 py-3.5",
          band?.rail
        )}
      >
        {steps.map((step, i) => {
          const active = i === activeIndex
          const done = i <= completedThrough && !active
          const clickable = typeof onJump === "function" && i <= completedThrough && !active

          const last = i === steps.length - 1
          const lineDone = i < activeIndex || i <= completedThrough

          return (
            <div key={step.id ?? i} className="flex flex-shrink-0 items-center">
              <button
                type="button"
                disabled={!clickable}
                onClick={clickable ? () => onJump(i) : undefined}
                className={cn(
                  "flex items-center gap-2.5 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
                  clickable ? "cursor-pointer" : "cursor-default"
                )}
              >
                <span
                  className={cn(
                    "flex size-7 shrink-0 items-center justify-center rounded-full border font-display text-[12px] font-bold transition-colors",
                    done && "border-primary bg-primary text-primary-foreground",
                    active && "border-primary bg-card text-primary ring-2 ring-primary/20",
                    !done && !active && "border-line-2 bg-card text-muted-foreground"
                  )}
                >
                  {done ? <Check className="size-3.5" strokeWidth={3} /> : i + 1}
                </span>
                <span
                  className={cn(
                    "hidden whitespace-nowrap text-[12.5px] font-semibold transition-colors sm:block",
                    active && "text-foreground",
                    done && "text-muted-foreground",
                    !done && !active && "text-muted-foreground"
                  )}
                >
                  {labelOf(step, i)}
                </span>
              </button>

              {!last && (
                <span
                  className={cn(
                    "mx-3 h-px w-6 min-w-[14px] flex-shrink-0 sm:w-10",
                    lineDone ? "bg-primary/35" : "bg-border"
                  )}
                />
              )}
            </div>
          )
        })}
      </div>
    </>
  )
}
